/**
 * Persist which camera phone records a Live Training station / subgroup.
 * Off-table chrono uses a remote session camera (player phone or table phone),
 * never the managing coach device's local webcam list.
 */

const STORAGE_PREFIX = "lt-station-camera:";

export const OFF_TABLE_CHRONO_SOURCE = "off_table_chrono";

export function stationCameraStorageKey(stationKey) {
  const key = String(stationKey || "").trim();
  return key ? `${STORAGE_PREFIX}${key}` : null;
}

/**
 * Prefer station (tsg) binding, then whole-group, then exercise.
 * @param {{ tsgId?: string|null, grId?: string|null, teId?: string|null }} ids
 */
export function resolveStationCameraKey({ tsgId = null, grId = null, teId = null } = {}) {
  if (tsgId != null && String(tsgId).trim()) return `tsg:${String(tsgId).trim()}`;
  if (grId != null && String(grId).trim()) return `gr:${String(grId).trim()}`;
  if (teId != null && String(teId).trim()) return `te:${String(teId).trim()}`;
  return null;
}

/**
 * @typedef {{
 *   kind: 'user',
 *   userId: string,
 *   playerId?: string|null,
 *   label?: string|null,
 *   tableNumber?: number|null,
 *   tsgId?: string|null,
 * } | {
 *   kind: 'table',
 *   tableNumber: number,
 *   label?: string|null,
 * } | {
 *   kind: 'station',
 *   tsgId: string,
 *   userId: string,
 *   playerId?: string|null,
 *   label?: string|null,
 * }} StationCameraTarget
 */

/** @param {unknown} raw */
export function normalizeStationCameraTarget(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const id = raw.trim();
    // Legacy local deviceId bindings are ignored for chrono (coach never records).
    if (!id || id.startsWith("cam-") || id.length > 40) return null;
    try {
      return normalizeStationCameraTarget(JSON.parse(id));
    } catch {
      return null;
    }
  }
  if (typeof raw !== "object") return null;
  const kind = String(raw.kind || "").trim().toLowerCase();
  if (kind === "station") {
    const tsgId = raw.tsgId != null ? String(raw.tsgId).trim() : "";
    const userId = raw.userId != null ? String(raw.userId).trim() : "";
    if (!tsgId || !userId) return null;
    return {
      kind: "station",
      tsgId,
      userId,
      playerId: raw.playerId != null ? String(raw.playerId) : null,
      label: raw.label != null ? String(raw.label) : null,
    };
  }
  if (kind === "user") {
    const userId = raw.userId != null ? String(raw.userId).trim() : "";
    if (!userId) return null;
    const tableNumber = Number(raw.tableNumber);
    const tsgId =
      raw.tsgId != null && String(raw.tsgId).trim()
        ? String(raw.tsgId).trim()
        : null;
    return {
      kind: "user",
      userId,
      playerId: raw.playerId != null ? String(raw.playerId) : null,
      label: raw.label != null ? String(raw.label) : null,
      tableNumber: Number.isFinite(tableNumber) && tableNumber > 0 ? tableNumber : null,
      tsgId,
    };
  }
  if (kind === "table") {
    const tableNumber = Number(raw.tableNumber);
    if (!Number.isFinite(tableNumber) || tableNumber <= 0) return null;
    return {
      kind: "table",
      tableNumber,
      label: raw.label != null ? String(raw.label) : null,
    };
  }
  return null;
}

/** @param {string|null|undefined} stationKey */
export function getStationCameraTarget(stationKey) {
  const storageKey = stationCameraStorageKey(stationKey);
  if (!storageKey || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    try {
      return normalizeStationCameraTarget(JSON.parse(raw));
    } catch {
      return normalizeStationCameraTarget(raw);
    }
  } catch {
    return null;
  }
}

/**
 * @param {string|null|undefined} stationKey
 * @param {StationCameraTarget|null|undefined} target
 */
export function setStationCameraTarget(stationKey, target) {
  const storageKey = stationCameraStorageKey(stationKey);
  if (!storageKey || typeof localStorage === "undefined") return;
  try {
    const next = normalizeStationCameraTarget(target);
    if (!next) localStorage.removeItem(storageKey);
    else localStorage.setItem(storageKey, JSON.stringify(next));
  } catch {
    /* private mode */
  }
}

/** @deprecated Prefer getStationCameraTarget — kept for count-panel / tests. */
export function getStationCameraDeviceId(stationKey) {
  const target = getStationCameraTarget(stationKey);
  if (!target) return null;
  return stationCameraTargetKey(target) || null;
}

/** @deprecated Prefer setStationCameraTarget */
export function setStationCameraDeviceId(stationKey, deviceId) {
  if (deviceId == null || String(deviceId).trim() === "") {
    setStationCameraTarget(stationKey, null);
    return;
  }
  const raw = String(deviceId).trim();
  if (raw.startsWith("user:")) {
    setStationCameraTarget(stationKey, {
      kind: "user",
      userId: raw.slice(5),
    });
    return;
  }
  if (raw.startsWith("table:")) {
    setStationCameraTarget(stationKey, {
      kind: "table",
      tableNumber: Number(raw.slice(6)),
    });
    return;
  }
  // Drop legacy local MediaDevice ids — they must not drive chrono recording.
  setStationCameraTarget(stationKey, null);
}

export function stationCameraTargetKey(target) {
  const t = normalizeStationCameraTarget(target);
  if (!t) return "";
  if (t.kind === "station") return `station:${t.tsgId}:${t.userId}`;
  if (t.kind === "user") return `user:${t.userId}`;
  if (t.kind === "table") return `table:${t.tableNumber}`;
  return "";
}

/**
 * @param {StationCameraTarget|null|undefined} target
 * @param {(key: string, opts?: object) => string} [t]
 * @param {{ stationName?: string|null }} [opts]
 */
export function stationCameraTargetLabel(target, t, opts = {}) {
  const cam = normalizeStationCameraTarget(target);
  if (!cam) return "";
  if (cam.label) return cam.label;
  if (cam.kind === "station") {
    const station = opts.stationName || cam.tsgId;
    return (
      (typeof t === "function"
        ? t("liveTrainingChronoStationCameraPhone", { station })
        : null) || `Station camera (${station})`
    );
  }
  if (cam.kind === "table") {
    return (
      (typeof t === "function"
        ? t("liveTrainingChronoTableCamera", { number: cam.tableNumber })
        : null) || `Table ${cam.tableNumber} camera`
    );
  }
  return cam.userId;
}

/** Recording identity (always a user or table phone the coach can start remotely). */
export function stationCameraRecordingIdentity(target) {
  const cam = normalizeStationCameraTarget(target);
  if (!cam) return null;
  if (cam.kind === "station") {
    return {
      kind: "user",
      userId: cam.userId,
      playerId: cam.playerId || null,
      tsgId: cam.tsgId,
      label: cam.label || null,
      tableNumber: null,
    };
  }
  return cam;
}
